import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Package, Gamepad2 } from "lucide-react";
import { api, rm, fmtDate, errMsg } from "../../lib/api";
import { useCart, useLang } from "../../lib/ctx";

export function PaymentResult() {
  const { pathname } = useLocation();
  const [sp] = useSearchParams();
  const { t } = useLang();
  const { clear } = useCart();
  const [st, setSt] = useState("pending");
  const [num, setNum] = useState("");
  const tries = useRef(0);
  const cancelled = pathname.includes("cancel");
  useEffect(() => {
    if (cancelled) return;
    const sid = sp.get("session_id");
    const tp = sp.get("provider") === "toyyibpay" ? sp.get("order") : null;
    const poll = async () => {
      try {
        const { data } = await api.get(tp ? `/payments/toyyibpay/status/${tp}` : `/payments/status/${sid}`);
        setNum(data.order_number);
        if (data.payment_status === "paid") { setSt("paid"); clear(); return; }
        if (["expired", "failed"].includes(data.payment_status)) return setSt("failed");
      } catch (e) { /* retry */ }
      if (++tries.current < 15) setTimeout(poll, 2000); else setSt("timeout");
    };
    poll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const last = JSON.parse(localStorage.getItem("ddv_last_order") || "{}");
  return (
    <div className="mx-auto max-w-xl px-4 py-24 text-center" data-testid="payment-result-page">
      {cancelled ? <XCircle className="mx-auto h-14 w-14 text-zinc-500" /> : st === "paid" ? <CheckCircle2 className="mx-auto h-14 w-14 text-[#C8A45D]" /> : st === "pending" ? <Loader2 className="mx-auto h-14 w-14 animate-spin text-[#C8A45D]" /> : <XCircle className="mx-auto h-14 w-14 text-red-400" />}
      <h1 className="mt-6 font-display text-5xl" data-testid="payment-status-title">{cancelled ? t("paymentCancel") : st === "paid" ? t("paymentSuccess") : st === "pending" ? t("paymentPending") : "Payment not confirmed"}</h1>
      {num && <p className="mt-3 font-mono text-sm text-zinc-400" data-testid="payment-order-number">{num}</p>}
      <p className="mt-4 text-sm text-zinc-400">{cancelled ? "Your reserved items are released after 30 minutes if unpaid." : st === "paid" ? "Your invoice and receipt are ready in Track Order and are emailed to you." : st === "timeout" ? "We could not confirm yet. Check Track Order in a minute." : ""}</p>
      <div className="mt-8 flex justify-center gap-3">
        <Link to={`/track-order?number=${num || last.number || ""}&email=${last.email || ""}`} className="btn-gold" data-testid="payment-track-btn">{t("track")}</Link>
        <Link to="/shop" className="btn-ghost">{t("continueShopping")}</Link>
      </div>
    </div>
  );
}

const STEPS = ["paid", "processing", "shipped", "delivered"];

export const OrderView = ({ o }) => {
  const idx = Math.max(STEPS.indexOf(o.status), o.status === "completed" ? 3 : -1);
  return (
    <div className="card-dark mt-10 p-6 text-left" data-testid="order-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><p className="font-mono text-sm text-[#C8A45D]" data-testid="order-number">{o.number}</p><p className="text-xs text-zinc-500">{fmtDate(o.created_at)}</p></div>
        <span className="border border-[#C8A45D] px-3 py-1 font-mono text-xs uppercase text-[#C8A45D]" data-testid="order-status">{o.status.replace(/_/g, " ")}</span>
      </div>
      {o.items.some((i) => i.type === "physical") && (
        <div className="mt-8 grid grid-cols-4 gap-2">{STEPS.map((s, i) => <div key={s}><div className={`h-1 ${i <= idx ? "bg-[#C8A45D]" : "bg-white/10"}`} /><p className="mt-2 font-mono text-[10px] uppercase text-zinc-400">{s}</p></div>)}</div>
      )}
      {o.shipment?.tracking_no && <p className="mt-6 flex items-center gap-2 text-sm" data-testid="order-tracking-no"><Package className="h-4 w-4 text-[#C8A45D]" /> {o.shipment.carrier}: <b>{o.shipment.tracking_no}</b> · {o.shipment.status}{o.shipment.tracking_url && <a href={o.shipment.tracking_url} target="_blank" rel="noreferrer" className="text-[#C8A45D] underline" data-testid="order-tracking-link">Track</a>}</p>}
      {o.shipment?.events?.length > 0 && <ul className="mt-3 space-y-1 border-l border-white/10 pl-4 text-xs text-zinc-400">{o.shipment.events.map((e, i) => <li key={i}>{fmtDate(e.at)} — {e.status}: {e.note}</li>)}</ul>}
      {o.game_orders?.map((g) => <p key={g.id} className="mt-4 flex items-center gap-2 text-sm" data-testid="order-game-status"><Gamepad2 className="h-4 w-4 text-[#C8A45D]" /> {g.title} · ID {g.player_id} — <b className="uppercase">{g.status.replace(/_/g, " ")}</b></p>)}
      <div className="mt-6 space-y-1 border-t border-white/10 pt-4 text-sm">
        {o.items.map((i, k) => <div key={k} className="flex justify-between"><span>{i.title} {i.size} × {i.qty}</span><span>{rm(i.unit_price * i.qty)}</span></div>)}
        <div className="flex justify-between font-semibold"><span>Total</span><span>{rm(o.total)}</span></div>
      </div>
      {o.invoice_no && <Link to={`/invoice/${o.number}?email=${o.email}`} className="mt-4 inline-block text-xs uppercase tracking-widest text-[#C8A45D]" data-testid="order-invoice-link">View invoice / receipt</Link>}
    </div>
  );
};

export function TrackOrder() {
  const { t } = useLang();
  const [sp] = useSearchParams();
  const [number, setNumber] = useState(sp.get("number") || "");
  const [email, setEmail] = useState(sp.get("email") || "");
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const go = async (e) => {
    e?.preventDefault();
    setErr("");
    try { setO((await api.get("/orders/track", { params: { number, email } })).data); } catch (x) { setO(null); setErr(errMsg(x)); }
  };
  useEffect(() => {
    if (number && email) go();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="mx-auto max-w-3xl px-4 py-20" data-testid="track-order-page">
      <h1 className="font-display text-5xl sm:text-6xl">{t("trackTitle")}</h1>
      <form onSubmit={go} className="mt-8 grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
        <input value={number} onChange={(e) => setNumber(e.target.value)} placeholder={t("orderNumber")} required className="field" data-testid="track-number-input" />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("email")} required className="field" data-testid="track-email-input" />
        <button className="btn-gold" data-testid="track-submit-btn">{t("trackBtn")}</button>
      </form>
      {err && <p className="mt-4 text-sm text-red-400" data-testid="track-error">{err}</p>}
      {o && <OrderView o={o} />}
    </div>
  );
}
